import { useEffect, useState } from 'react'
import { getToken } from '@/lib/token'
import { ImageLightbox } from './ImageLightbox'

interface Props {
  path: string
  sessionId: string
  className?: string
  alt?: string
  enableLightbox?: boolean
}

export function AuthImage({ path, sessionId, className, alt, enableLightbox }: Props) {
  const [src, setSrc] = useState<string | null>(null)
  const [error, setError] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  useEffect(() => {
    setSrc(null)
    setError(false)
    setLightboxOpen(false)

    let mounted = true
    let objectUrl: string | null = null

    const filename = path.split('/').pop()
    if (!filename) {
      setError(true)
      return
    }

    const token = getToken()
    fetch(`/v1/sessions/${sessionId}/uploads/${filename}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((resp) => {
        if (!resp.ok) throw new Error('Failed to load image')
        return resp.blob()
      })
      .then((blob) => {
        if (!mounted) return
        objectUrl = URL.createObjectURL(blob)
        setSrc(objectUrl)
      })
      .catch(() => {
        if (mounted) setError(true)
      })

    return () => {
      mounted = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [path, sessionId])

  if (error) {
    return (
      <span className="text-xs text-ember">
        Failed to load image
      </span>
    )
  }

  if (!src) {
    return (
      <div className={`${className || ''} bg-haze/30 animate-pulse rounded-xl`} />
    )
  }

  return (
    <>
      <img
        src={src}
        alt={alt || 'Uploaded image'}
        className={`${className || ''} cursor-pointer hover:scale-[1.02] transition-transform duration-300`}
        onClick={() => enableLightbox && setLightboxOpen(true)}
      />
      {lightboxOpen && (
        <ImageLightbox src={src} alt={alt} onClose={() => setLightboxOpen(false)} />
      )}
    </>
  )
}
